import React, { VFC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileItem } from '@dxb-web-ui-framework/design-system';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { TransactionServiceImpl } from '../../../../../transaction/src/domain/Transaction/usecases';
import { TransactionDocumentData } from '../../../../../transaction/src/domain/Transaction/entities/TransactionDocuments';
import { MimeType } from '../../../utils/mimeTypesToDownload';
import { downloadTransactionDetailsFile } from '../../../utils/fileHelpers';

export type TransactionDocumentProps = {
  transactionService: TransactionServiceImpl;
  customerId: Nullable<string>;
  fileName: string;
  fileSn: number;
  referenceNumber: string;
  onRefetchData: () => void;
  shouldHideDeleteButton?: boolean;
};

const TransactionDoc: VFC<TransactionDocumentProps> = ({
  transactionService,
  customerId,
  fileName,
  fileSn,
  referenceNumber,
  onRefetchData,
  shouldHideDeleteButton,
}) => {
  const { t } = useTranslation();
  const [timestamp, setTimestamp] = useState<Nullable<string>>(null);
  const [action, setAction] = useState<Nullable<'download' | 'delete'>>(null);
  const shouldDownloadDocument = action === 'download' && Boolean(timestamp);
  const { data: transactionDocumentData, isLoading: isLoadingDownloadDocument } =
    useInvokeSWRService<TransactionDocumentData>(
      transactionService.GetTransactionDocumentData,
      {
        customerId,
        fileSn,
        referenceNumber,
        meta: { timestamp },
      },
      shouldDownloadDocument,
    );

  const shouldDeleteDocument = action === 'delete' && Boolean(timestamp);
  const { isLoading: isLoadingDeleteDocument } = useInvokeSWRService(
    transactionService.DeleteDocument,
    {
      customerId,
      fileSn,
      referenceNumber,
      successCallback: onRefetchData,
    },
    shouldDeleteDocument,
    'transactionService.DeleteDocument',
  );

  useEffect(() => {
    if (transactionDocumentData) {
      downloadTransactionDetailsFile(
        fileName,
        transactionDocumentData.fileData,
        transactionDocumentData?.extension?.split('.')[1] as MimeType,
      );
    }
  }, [fileName, transactionDocumentData]);

  const handleClickFile = (buttonAction: 'download' | 'delete') => {
    setTimestamp(null);
    setAction(buttonAction);
    setTimestamp(new Date().toISOString());
  };

  const actionButtons = [
    { type: 'download', onClick: () => handleClickFile('download'), title: t('FileItem.Download.Action') },
  ];

  if (actionButtons.length === 1 && !shouldHideDeleteButton) {
    actionButtons.push({ type: 'close', onClick: () => handleClickFile('delete'), title: t('FileItem.Delete.Action') });
  }

  const isLoading = isLoadingDownloadDocument || isLoadingDeleteDocument;
  return <FileItem hasSkeleton={isLoading} key={referenceNumber} filename={fileName} state={actionButtons} />;
};

export { TransactionDoc };
